import { ReactNode, useState } from 'react'
import {
  Box,
  Skeleton,
  Stack,
  styled,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'

import classNames from 'classnames'
import './List.css'
import { COLORS } from 'app-constants'

export const ListTable = styled(Table)`
  border-spacing: 0 8px;
  border-collapse: separate;
`

export const BodyRow = styled(TableRow)`
  td:first-of-type {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  td:last-of-type {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }
`

export const BodyCell = styled(TableCell)`
  border: none;
  height: 48px;
  min-width: 8px;
  max-width: 120px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  padding: 0 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #222222;
`

export const HeaderRow = styled(TableRow)``

export const HeaderCell = styled(TableCell)`
  border: none;
  padding: 0 8px;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
`

export interface Row {
  key: string
  columnEntry: (column: number) => ReactNode | null
  columnWidth: (column: number) => string | number | undefined
}

export interface ListRenderer {
  rows: Row[]
  rowHeight?: number
  columnCount: number
  columnName?: (column: number) => string | null | React.ReactNode
  onRowClick?: (rowIndex: number) => void
}

export enum SortDirection {
  Ascending,
  Descending,
}

export const HoverRow = ({ children, index }: { children: ReactNode; index: number }) => {
  const [isHovering, setIsHovering] = useState(false)

  const onMouseEnter = () => {
    setIsHovering(true)
  }

  const onMouseLeave = () => {
    setIsHovering(false)
  }

  const classes = classNames({
    isHovering: isHovering,
    BodyRowDefaultColor: true,
  })

  return (
    <BodyRow onMouseEnter={onMouseEnter} onMouseMove={onMouseEnter} onMouseLeave={onMouseLeave} className={classes}>
      {children}
    </BodyRow>
  )
}

const List = ({
  renderer,
  isLoading,
  loadingRowCount,
  sx,
  sortDirection,
  sortedColumnIndex,
  onColumnHeaderClick,
  sortableColumnIndices,
}: {
  renderer: ListRenderer
  isLoading: boolean
  loadingRowCount: number
  sx?: SxProps<Theme> | undefined
  sortDirection?: SortDirection
  sortedColumnIndex?: number
  onColumnHeaderClick?: (columnIndex: number) => void
  sortableColumnIndices?: number[]
}) => {
  const renderTable = () => {
    let columnIndexArray = Array.from(Array(renderer.columnCount).keys())
    const getSortIcon = (index: number) => {
      if (sortedColumnIndex !== index) {
        return null
      }
      return sortDirection === SortDirection.Ascending ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />
    }

    return (
      <ListTable sx={sx}>
        <TableHead>
          {renderer.columnName && (
            <HeaderRow>
              {columnIndexArray.map((i) => (
                <HeaderCell
                  key={i}
                  onClick={() => sortableColumnIndices?.includes(i) && onColumnHeaderClick?.(i)}
                  sx={{
                    cursor: sortableColumnIndices?.includes(i) && onColumnHeaderClick ? 'pointer' : 'inherit',
                    whiteSpace: 'nowrap',
                  }}>
                  <Stack direction="row" alignItems="center">
                    <Typography variant="h6">{renderer.columnName!(i)}</Typography>
                    {getSortIcon(i)}
                  </Stack>
                </HeaderCell>
              ))}
            </HeaderRow>
          )}
        </TableHead>
        <TableBody>
          {renderer.rows.map((row, index) => (
            <HoverRow key={row.key} index={index}>
              {columnIndexArray.map((i) => (
                <BodyCell key={i} sx={{ width: row.columnWidth(i), height: renderer.rowHeight }}>
                  {row.columnEntry(i)}
                </BodyCell>
              ))}
            </HoverRow>
          ))}
        </TableBody>
      </ListTable>
    )
  }

  const renderLoading = () => {
    let loadingRows = Array.from(Array(loadingRowCount).keys())
    let mt = renderer.columnName ? 7 : 1

    return (
      <Stack spacing={1} mt={mt} mb={1}>
        {loadingRows.map((i) => (
          <Skeleton
            variant="rectangular"
            height={renderer.rowHeight ?? 48}
            key={i}
            style={{ backgroundColor: COLORS.GRAY_8 }}
          />
        ))}
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        overflowX: 'auto',
      }}>
      {isLoading ? renderLoading() : renderTable()}
    </Box>
  )
}

List.defaultProps = {
  loadingRowCount: 8,
}

export default List
